import clsx from 'clsx';
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly label?: string | undefined;
  readonly icon?: LucideIcon | undefined;
  /** Server- or client-side validation message. Also sets aria-invalid. */
  readonly error?: string | undefined;
  readonly helperText?: string | undefined;
  readonly id?: string | undefined;
}

/**
 * Text input with a label that is always associated.
 *
 * The legacy app had 74 unassociated labels; `useId` removes the opportunity to forget.
 * `error` is wired to aria-describedby + aria-invalid — the prototype declared an
 * `error` prop and never used it anywhere.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    icon: Icon,
    error,
    helperText,
    id,
    required,
    className,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;
  const hasMessage = error !== undefined || helperText !== undefined;

  return (
    <div className={clsx(styles.group, className)}>
      {label !== undefined && (
        // The required marker is drawn by CSS, not inserted as text: otherwise it
        // becomes part of the accessible name ("Password *") for every assistive
        // technology and every test that looks the field up by its label.
        <label
          htmlFor={inputId}
          className={clsx(styles.label, required === true && styles.labelRequired)}
        >
          {label}
        </label>
      )}

      <div className={clsx(styles.wrapper, Icon && styles.hasIcon, error && styles.hasError)}>
        {Icon && <Icon className={styles.icon} size={16} aria-hidden="true" />}
        <input
          ref={ref}
          id={inputId}
          className={styles.field}
          required={required}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={hasMessage ? messageId : undefined}
          {...rest}
        />
      </div>

      {error !== undefined ? (
        <p id={messageId} className={styles.error} role="alert">
          {error}
        </p>
      ) : helperText !== undefined ? (
        <p id={messageId} className={styles.helper}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
});
